import PropTypes from 'prop-types'
import { ImSpinner9 } from 'react-icons/im'
import {
  LuMoveRight, LuX, LuFileText, LuBookCheck, LuSend, LuBanknote,
  LuBadgeEuro, LuShield, LuBan, LuDownload, LuSearchCheck, LuTruck,
  LuSignature, LuLock, LuCircleX, LuFileClock, LuCircleHelp, LuMailQuestion
} from 'react-icons/lu'
import { useLocation } from 'wouter'
import { useModal } from 'contexts/ModalContext'

const ESTADOS_SINIESTRO = [
  "ENVIADO CHUBB",
  "PENDIENTE PAGO BA",
  'REVISADO',
  'ACEPTADO CHUBB',
  'ENVIO FINIQUITO MRW',
  'RECIBO FINIQUITO FIRMADO',
  'LIQUIDADO',
  'CERRADO',
  'BAJO FRANQUICIA',
  'CANCELADO',
  'PENDIENTE DOC MRW',
  'DOC PEDIDA',
  'SIN ESTADO',
  "NO TRAMITABLE"
]

const ICON_BY_ESTADO = {
  'ENVIADO CHUBB':           LuSend,
  'PENDIENTE PAGO BA':       LuBanknote,
  'REVISADO':                LuSearchCheck,
  'ACEPTADO CHUBB':          LuBookCheck,
  'ENVIO FINIQUITO MRW':     LuTruck,
  'RECIBO FINIQUITO FIRMADO':LuSignature,
  'LIQUIDADO':               LuBadgeEuro,
  'CERRADO':                 LuLock,
  'BAJO FRANQUICIA':         LuShield,
  'CANCELADO':               LuCircleX,
  'PENDIENTE DOC MRW':       LuFileClock,
  'DOC PEDIDA':              LuMailQuestion,
  'SIN ESTADO':              LuCircleHelp,
  'NO TRAMITABLE':           LuBan,
}

const circleClass =
  'flex items-center justify-center w-20 h-20 rounded-full bg-[#FCAE52]'

const chunkArray = (arr, size) => {
  const result = []
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size))
  }
  return result
}

function downloadExcel(cards, firstYear, lastYear) {
  import('xlsx').then((XLSX) => {
    const headers = [
      'Estado',
      'Año 2024',
      'Año 2025',
      'Año 2026',
      `Histórico (${firstYear} - ${lastYear})`
    ]

    const rows = cards.map((c) => [
      c.estado,
      c.total_2024,
      c.total_2025,
      c.total_2026,
      c.total
    ])

    // Fila de totales
    rows.push([
      'TOTAL',
      cards.reduce((s, c) => s + c.total_2024, 0),
      cards.reduce((s, c) => s + c.total_2025, 0),
      cards.reduce((s, c) => s + c.total_2026, 0),
      cards.reduce((s, c) => s + c.total, 0)
    ])

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
    ws['!cols'] = [{ wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 24 }]

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Dashboard Siniestros')
    XLSX.writeFile(wb, 'dashboard_siniestros.xlsx')
  }).catch(() => {
    console.error('No se pudo cargar la librería xlsx para exportar')
  })
}

export default function DashboardComp({ stats }) {
  const [, setLocation] = useLocation()
  const { openModal } = useModal()

  if (stats?.loading) {
    return (
      <div className="flex justify-center mt-10">
        <ImSpinner9 size={40} className="animate-spin" />
      </div>
    )
  }

  if (stats?.error) {
    return (
      <div className="flex items-center justify-center mt-10 text-red-600">
        <LuX size={22} />
        <span className="ml-2">{stats?.error}</span>
      </div>
    )
  }

  if (!stats?.data) return null

  const firstYear = stats?.data?.range?.first_year != null
    ? Number(stats.data.range.first_year)
    : 2024
  const lastYear = stats?.data?.range?.last_year != null
    ? Number(stats.data.range.last_year)
    : 2026

  const map = new Map(
    (stats?.data?.rows || []).map((r) => [String(r?.estado_siniestro || ''), r])
  )

  // Agrupa en una sola fila todos los registros sin estado (NULL o vacío)
  const sinEstadoRows = (stats?.data?.rows || []).filter(
    (r) => !r?.estado_siniestro || String(r.estado_siniestro).trim() === ''
  )
  const sinEstadoAgg = sinEstadoRows.reduce(
    (acc, r) => ({
      total:      acc.total      + Number(r.total      || 0),
      total_2024: acc.total_2024 + Number(r.total_2024 || 0),
      total_2025: acc.total_2025 + Number(r.total_2025 || 0),
      total_2026: acc.total_2026 + Number(r.total_2026 || 0),
    }),
    { total: 0, total_2024: 0, total_2025: 0, total_2026: 0 }
  )

  const cards = ESTADOS_SINIESTRO.map((estado) => {
    if (estado === 'SIN ESTADO') {
      return { estado, ...sinEstadoAgg }
    }
    const row = map.get(estado)
    return {
      estado,
      total_2024: Number(row?.total_2024 || 0),
      total_2025: Number(row?.total_2025 || 0),
      total_2026: Number(row?.total_2026 || 0),
      total:      Number(row?.total      || 0)
    }
  })

  const rows = chunkArray(cards, 4)

  return (
    <div className="h-fit dark:bg-gray-900">
      {/* Botón descargar Excel */}
      <div className="w-full md:px-6 pt-6 flex justify-end">
        <button
          onClick={() => downloadExcel(cards, firstYear, lastYear)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FCAE52] hover:bg-[#e89e42] text-white font-medium text-sm transition-colors shadow"
        >
          <LuDownload size={16} />
          Descargar Excel
        </button>
      </div>

      <div className="w-full md:px-6 md:py-6 h-fit">
        {rows.map((rowCards, rowIdx) => (
          <div
            key={rowIdx}
            className="grid w-full grid-cols-12 mt-8 gap-y-8 md:gap-8 xl:mt-12 justify-items-center"
          >
            {rowCards.map((item) => {
              const Icon = ICON_BY_ESTADO[item.estado] || LuFileText
              return (
                <div
                  key={item.estado}
                  className="w-full max-w-md col-span-12 px-8 py-4 mt-16 bg-white rounded-lg shadow-lg md:col-span-6 xl:col-span-3 dark:bg-gray-800"
                >
                  {/* Icono flotando */}
                  <div className="flex items-center justify-center -mt-16 md:justify-end">
                    <div className={circleClass}>
                      <Icon className="w-11 h-11 text-black" />
                    </div>
                  </div>

                  {/* Título */}
                  <h2 className="mt-2 text-xl font-semibold text-gray-800 dark:text-white md:mt-0">
                    {item.estado}
                  </h2>

                  <div className="flex flex-col gap-1 mt-2">
                    {[
                      { year: 2024, value: item.total_2024 },
                      { year: 2025, value: item.total_2025 },
                      { year: 2026, value: item.total_2026 },
                    ].map(({ year, value }) => (
                      <div key={year} className="flex flex-wrap items-center text-sm text-gray-600 dark:text-gray-200">
                        Año {year}:{' '}
                        <span className="font-semibold underline decoration-[#FCAE52] px-1">
                          {stats?.loading ? <ImSpinner9 size={16} className="animate-spin" /> : value}
                        </span>
                        siniestros.
                      </div>
                    ))}

                    {/* Histórico */}
                    <div className="flex flex-wrap items-center text-sm text-gray-600 dark:text-gray-200">
                      Histórico ({firstYear} - {lastYear}):{' '}
                      <span className="font-semibold underline decoration-[#FCAE52] px-1">
                        {stats?.loading ? <ImSpinner9 size={16} className="animate-spin" /> : item.total}
                      </span>
                      siniestros.
                    </div>
                  </div>

                  {/* CTA Ir */}
                  <div className="flex justify-end mt-4">
                    <button
                      type="button"
                      title={`Ver siniestros (${item.estado})`}
                      onClick={() => {
                        if (!item.total) {
                          openModal('EmptyEstadoModal', { estado: item.estado })
                          return
                        }
                        setLocation(
                          item.estado === 'SIN ESTADO'
                            ? '/siniestros/grid?estado_siniestro=null'
                            : `/siniestros/grid?estado_siniestro=${encodeURIComponent(item.estado)}`
                        )
                      }}
                      className="flex items-center justify-center space-x-2 font-medium text-[#FCAE52] dark:text-[#FCAE52]"
                    >
                      <span>Ir</span>
                      <LuMoveRight size={20} />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}

DashboardComp.propTypes = {
  stats: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.any,
    data: PropTypes.shape({
      range: PropTypes.shape({
        first_year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        last_year:  PropTypes.oneOfType([PropTypes.number, PropTypes.string])
      }),
      rows: PropTypes.arrayOf(
        PropTypes.shape({
          estado_siniestro: PropTypes.string,
          total:            PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
          total_2024:       PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
          total_2025:       PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
          total_2026:       PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        })
      )
    })
  })
}